import InfoIcon from "@mui/icons-material/InfoOutlined";
import { Collapse, Divider, InputAdornment, Tooltip } from "@mui/material";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ReactHookAutocomplete } from "core/components/ReactHookFormComponents/ReactHookAutocomplete";
import { ReactHookDatePicker } from "core/components/ReactHookFormComponents/ReactHookDatePicker";
import { ReactHookRadioGroup } from "core/components/ReactHookFormComponents/ReactHookRadioGroup";
import { ReactHookTextField } from "core/components/ReactHookFormComponents/ReactHookTextField";
import { ReactHookFreeSolo } from "core/components/ReactHookFreeSolo";
import type { SelectOption, TFunction } from "core/entities";
import { useVariableDebounced } from "core/hooks/useVariableDebounced";
import { jsonFetch } from "core/utils/jsonFetch";
import { addYears } from "date-fns/addYears";
import type {
  EstimateRequestPaymentMethod,
  EstimateRequestVehicleInformationFormValues,
} from "features/EstimateRequest/entities";
import { EstimateRequestImageUpload } from "features/EstimateRequest/pages/PublicEstimateRequestFormPage/components/EstimateRequestImageUpload";
import { useInsurerSelectOptions } from "features/PaymentRequest/hooks";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import useSWR from "swr";

export const VEHICLE_INFORMATION_BASE_URL =
  "https://public.opendatasoft.com/api/explore/v2.1/catalog/datasets/all-vehicles-model/records";

export function usePublicEstimateRequestVehicleInformationFormFormMethods() {
  return useForm<EstimateRequestVehicleInformationFormValues>({
    defaultValues: {
      vehicleMake: "",
      vehicleModel: "",
      vehicleYear: null,
      vinNumber: "",
      vinFileId: "",
      vinFileName: "",
      vinImage: null,
      paymentType: "CustomerPay",
      insurerId: "",
    },
  });
}

type VehicleInformationFormProps = {
  formMethods: ReturnType<
    typeof usePublicEstimateRequestVehicleInformationFormFormMethods
  >;
  t: TFunction;
  estimateRequestId: string; // guid
  shopId: string; // guid
  isInsurerPayActive: boolean;
};

export function PublicEstimateRequestVehicleInformationForm({
  formMethods,
  t,
  shopId,
  estimateRequestId,
  isInsurerPayActive,
}: VehicleInformationFormProps) {
  const payOptions = useMemo<SelectOption<EstimateRequestPaymentMethod>[]>(
    () => [
      { label: t("payOptions.customer", "Customer Pay"), value: "CustomerPay" },
      { label: t("payOptions.insurer", "Insurer Pay"), value: "InsurerPay" },
    ],
    [t]
  );
  const { insurersOptions, isLoading: isInsurerOptionsLoading } =
    useInsurerSelectOptions();
  const vehicleMake = formMethods.watch("vehicleMake");
  const makesUrl = new URL(VEHICLE_INFORMATION_BASE_URL);
  makesUrl.searchParams.set("group_by", "make");
  const { data: makeData } = useSWR<{ results: { make: string }[] }>(
    makesUrl.href,
    jsonFetch
  );
  const paymentType = formMethods.watch("paymentType");

  const makeOptions =
    makeData?.results
      .filter((result) => "make" in result)
      .map((result) => result.make) ?? [];

  const modelUrl = new URL(VEHICLE_INFORMATION_BASE_URL);
  modelUrl.searchParams.set("group_by", "model");

  if (vehicleMake) {
    modelUrl.searchParams.set("where", `make like '${vehicleMake}'`);
  } else {
    modelUrl.searchParams.set("limit", "99");
  }

  const modelUrlHref = useVariableDebounced(modelUrl.href, 200);

  const { data: modelData } = useSWR<{ results: { model: string }[] }>(
    modelUrlHref,
    jsonFetch
  );
  const modelOptions =
    modelData?.results
      ?.filter((result) => "model" in result)
      .map((result) => result.model) ?? [];

  const minYearDate = new Date(1900, 0, 1);
  const maxYearDate = addYears(new Date(), 1);

  const vehicleYearErrorMessage = t("vehicleYearError", {
    minYear: minYearDate.getFullYear(),
    maxYear: maxYearDate.getFullYear(),
  });

  const ToolTipTitle = (
    <>
      <Typography variant="subtitle2">{t("vinUploadTitle")}</Typography>
      <ul>
        <li>
          <Typography variant="body2">{t("vinUploadTooltip1")}</Typography>
        </li>
        <li>
          <Typography variant="body2">{t("vinUploadTooltip2")}</Typography>
        </li>
      </ul>
    </>
  );
  return (
    <Stack gap={2} component="form">
      <ReactHookFreeSolo
        required
        control={formMethods.control}
        name="vehicleMake"
        label={t("vehicleMake")}
        options={makeOptions}
        autoFocus
      />
      <ReactHookFreeSolo
        required
        disabled={vehicleMake === ""}
        control={formMethods.control}
        name="vehicleModel"
        label={t("vehicleModel")}
        options={modelOptions}
      />
      <ReactHookDatePicker
        control={formMethods.control}
        label={t("vehicleYear")}
        name="vehicleYear"
        required
        rules={{
          validate: {
            isValidYear: (value) => {
              if (!value) {
                return true;
              }
              const selectedYear = value.getFullYear();
              const minYear = minYearDate.getFullYear();
              const maxYear = maxYearDate.getFullYear();
              if (selectedYear > maxYear) {
                return vehicleYearErrorMessage;
              }
              if (selectedYear < minYear) {
                return vehicleYearErrorMessage;
              }
              return true;
            },
          },
        }}
        datePickerProps={{
          views: ["year"],
          maxDate: maxYearDate,
          minDate: minYearDate,
        }}
      />
      <ReactHookTextField
        name="vinNumber"
        label={t("vinNumber")}
        control={formMethods.control}
        required
        rules={{
          validate: {
            isValidVin: (value) => {
              if (value) {
                const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
                if (!vinRegex.test(value)) {
                  return t("vinNumberInvalid");
                }
              }
              return true;
            },
          },
        }}
        textFieldProps={{
          slotProps: {
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title={ToolTipTitle}>
                    <InfoIcon />
                  </Tooltip>
                </InputAdornment>
              ),
            },
          },
        }}
      />
      <EstimateRequestImageUpload
        control={formMethods.control}
        estimateRequestId={estimateRequestId}
        shopId={shopId}
        fileFieldName="vinImage"
        fileNameFieldName="vinFileName"
        fileIdFieldName="vinFileId"
        uploadLabel={t("vinUpload")}
        location="Vin"
        required={false}
        imageNumber={1}
        imagePlaceholderSlot={
          <Tooltip title={ToolTipTitle}>
            <Stack
              color={
                formMethods.getFieldState("vinImage")?.error
                  ? "error.main"
                  : "text.primary"
              }
              direction="row"
              gap={1}
              flex={1}
              justifyContent="center"
            >
              <Typography>{t("vinUploadPlaceholder")}</Typography>
              <InfoIcon fontSize="small" />
            </Stack>
          </Tooltip>
        }
      />
      {isInsurerPayActive && (
        <>
          <Divider />
          <ReactHookRadioGroup
            control={formMethods.control}
            name="paymentType"
            label={t("payOptions.label", "Who will be paying for the repair?")}
            options={payOptions}
          />
          <Collapse in={paymentType === "InsurerPay"}>
            <ReactHookAutocomplete
              control={formMethods.control}
              name="insurerId"
              label={t("insurer", "Insurer")}
              required={isInsurerPayActive && paymentType === "InsurerPay"}
              options={insurersOptions}
              loading={isInsurerOptionsLoading}
              disabled={paymentType !== "InsurerPay"}
            />
          </Collapse>
        </>
      )}
    </Stack>
  );
}
